import { useState, type FormEvent } from "react";
import { ArrowRight, Check, LoaderCircle, ShieldCheck } from "lucide-react";
import { api } from "../api";

export function Login({
  setup,
  onLogin,
}: {
  setup: boolean;
  onLogin: () => void;
}) {
  const [password, setPassword] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api(`/auth/${setup ? "setup" : "login"}`, "POST", { password });
      if (location.hash.startsWith("#setup="))
        history.replaceState({}, "", location.pathname + location.search);
      onLogin();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="login-page">
      <div className="login-story">
        <div className="brand">
          <img src="/favicon.svg" alt="" />
          <span>
            Mini Kanban
            <span className="brand-sub">
              A little structure. A lot of context.
            </span>
          </span>
        </div>
        <div className="login-message">
          <span className="eyebrow">YOUR AGENTS. ONE SHARED MEMORY.</span>
          <h1>
            Good work deserves
            <br />a clear handoff.
          </h1>
          <p>
            A home for decisions, progress, and the next step.
            <br />
            Pick up exactly where the last session left off.
          </p>
          <div className="sample-handoff">
            <div>
              <span className="dot green" /> WHERE IT STANDS{" "}
              <span className="mono">KNB-3</span>
            </div>
            <h3>Context stays. Sessions come and go.</h3>
            <p>
              <Check size={15} /> The work, the reasoning, and what was tried.
            </p>
            <p>
              <ArrowRight size={15} /> A clear next step for whoever comes next.
            </p>
            <footer>
              <span className="avatar tiny">A</span> You stay in control.
            </footer>
          </div>
        </div>
        <span className="login-footer">
          Built for thoughtful work, one instruction at a time.
        </span>
      </div>
      <div className="login-form-wrap">
        <form onSubmit={submit} className="login-form">
          <span className="login-icon">
            <ShieldCheck size={25} />
          </span>
          <h2>{setup ? "Make yourself at home." : "Welcome back, Andika."}</h2>
          <p>
            {setup
              ? "Choose your admin password to open your board."
              : "Your board is ready when you are."}
          </p>
          <label>
            Admin password
            <input
              autoFocus
              type="password"
              autoComplete={setup ? "new-password" : "current-password"}
              minLength={setup ? 12 : undefined}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={
                setup ? "At least 12 characters" : "Enter your password"
              }
            />
          </label>
          {error && (
            <div className="form-error" role="alert">
              {error}
            </div>
          )}
          <button className="button primary large" disabled={busy}>
            {busy ? <LoaderCircle size={17} className="spin" /> : null}
            {setup ? "Create my board login" : "Open my board"}
            <ArrowRight size={16} />
          </button>
          <small>
            <ShieldCheck size={13} /> Your approval is required to mark work
            Done.
          </small>
        </form>
      </div>
    </div>
  );
}
